/**
 * The rich-text body of the composer: a toolbar in the app, an editor in a
 * sandboxed frame, and messages between them.
 *
 * The frame is the same arrangement as the reader's -- `allow-scripts` and
 * never `allow-same-origin`, one script by hash -- because it holds the same
 * thing: markup somebody else wrote. See `lib/editorFrame.ts`.
 *
 * The toolbar lives out here rather than inside the frame so that it is drawn
 * by the app's stylesheet and reads the app's state (`disabled` while sending,
 * the theme). Every button is one message; the frame answers with its state
 * after each, so the buttons show what is in force at the caret.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";

import {
  EDITOR_HASH,
  editorDocument,
  type EditorCommand,
  type EditorContent,
  type EditorMessage,
  type EditorState,
} from "../lib/editorFrame";
import { isDark, loadRemoteImages } from "../lib/settings";
import * as Icon from "./Icons";

interface Props {
  /** The markup to edit. Read once, when the frame loads: edits flow out. */
  initialHtml: string;
  /** `cid` → `data:` URI, for the pictures in a quoted original. */
  cids: Record<string, string>;
  disabled: boolean;
  /** `baseline` on the first report after a load: the same message, re-said. */
  onChange: (content: EditorContent, baseline: boolean) => void;
  onBlur: () => void;
  onEscape: () => void;
  /** The toolbar's last button: the way back to plain text. */
  onPlainText: () => void;
}

const IDLE: EditorState = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  ul: false,
  ol: false,
  quote: false,
  link: false,
};

export function RichEditor(p: Props) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [state, setState] = useState<EditorState>(IDLE);
  const [linking, setLinking] = useState(false);
  const [url, setUrl] = useState("");
  const linkInput = useRef<HTMLInputElement>(null);
  // The latest callbacks, read by a listener registered once. Re-registering
  // on every render would miss a message sent between two registrations.
  const latest = useRef(p);
  latest.current = p;
  /** Set by `init`, cleared by the first report that follows it. */
  const awaitingBaseline = useRef(false);
  /** Whether `init` has been sent; the baseline is awaited from the first. */
  const initialised = useRef(false);
  // Theme and remote-image choice at mount. A change afterwards reaches the
  // frame as a message rather than as a new document, which would lose the
  // caret and everything typed since the last report.
  const [doc] = useState(() => editorDocument(isDark.value, loadRemoteImages.value));

  const send = (msg: object) => frame.current?.contentWindow?.postMessage(msg, "*");
  const command = (name: EditorCommand, arg?: string) => send({ t: "cmd", name, arg });

  /**
   * Hand the frame its content. Sent twice: on the frame's `ready`, and on
   * its `load` event, which follows the script that posts `ready`. Either
   * alone can be missed -- `ready` when it arrives before the listener below
   * is in place, which left a reply's editor blank on the desktop; `load`
   * when an engine fires one for the blank page a frame starts on. The frame
   * acts on the first `init` it receives and ignores the rest.
   */
  const initialise = () => {
    if (!frame.current?.contentWindow) return;
    if (!initialised.current) awaitingBaseline.current = true;
    initialised.current = true;
    const cur = latest.current;
    send({ t: "init", html: cur.initialHtml, cids: cur.cids, dark: isDark.value });
  };

  // A layout effect, so the listener is in place in the same task that
  // inserts the frame -- before the frame can have loaded and spoken. A plain
  // effect waits for a paint, and a frame that announced itself in between
  // was never answered.
  useLayoutEffect(() => {
    const onMessage = (e: MessageEvent) => {
      // Only this frame's own window. The data is from an opaque origin, so
      // `e.origin` is "null" and says nothing; the source is what identifies it.
      if (e.source !== frame.current?.contentWindow) return;
      const m = e.data as EditorMessage | null;
      if (!m || typeof m !== "object" || typeof m.t !== "string") return;
      const cur = latest.current;
      switch (m.t) {
        case "ready":
          initialise();
          break;
        case "change": {
          const baseline = awaitingBaseline.current;
          awaitingBaseline.current = false;
          cur.onChange(
            {
              html: String(m.html ?? ""),
              text: String(m.text ?? ""),
              written: String(m.written ?? ""),
            },
            baseline,
          );
          break;
        }
        case "state":
          setState({
            bold: !!m.bold,
            italic: !!m.italic,
            underline: !!m.underline,
            strike: !!m.strike,
            ul: !!m.ul,
            ol: !!m.ol,
            quote: !!m.quote,
            link: !!m.link,
          });
          break;
        case "blur":
          cur.onBlur();
          break;
        case "escape":
          cur.onEscape();
          break;
        default:
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // The theme follows the app while the editor is open.
  useEffect(() => {
    send({ t: "theme", dark: isDark.value });
  }, [isDark.value]);

  useEffect(() => {
    if (linking) linkInput.current?.focus();
  }, [linking]);

  const link = () => {
    if (state.link) {
      command("unlink");
      return;
    }
    setUrl("");
    setLinking(true);
  };
  const confirmLink = () => {
    setLinking(false);
    if (url.trim()) command("link", url);
    else send({ t: "focus" });
  };

  const Button = ({
    cmd,
    on,
    label,
    children,
  }: {
    cmd: EditorCommand | (() => void);
    on?: boolean;
    label: string;
    children: preact.ComponentChildren;
  }) => (
    <button
      type="button"
      class="icon-btn"
      aria-label={label}
      title={label}
      aria-pressed={on}
      disabled={p.disabled}
      // Mouse down, not click: a click moves focus to the button first, and
      // the frame's selection would already be gone by the time it is asked.
      onMouseDown={(e) => {
        e.preventDefault();
        if (typeof cmd === "function") cmd();
        else command(cmd);
      }}
    >
      {children}
    </button>
  );

  return (
    <div class="editor">
      <div class="editor-bar" role="toolbar" aria-label="Formatting">
        <Button cmd="bold" on={state.bold} label="Bold">
          <Icon.Bold />
        </Button>
        <Button cmd="italic" on={state.italic} label="Italic">
          <Icon.Italic />
        </Button>
        <Button cmd="underline" on={state.underline} label="Underline">
          <Icon.Underline />
        </Button>
        <Button cmd="strike" on={state.strike} label="Strikethrough">
          <Icon.Strike />
        </Button>
        <span class="editor-sep" />
        <Button cmd={link} on={state.link} label={state.link ? "Remove link" : "Link"}>
          <Icon.Link />
        </Button>
        <Button cmd="ul" on={state.ul} label="Bulleted list">
          <Icon.ListBullets />
        </Button>
        <Button cmd="ol" on={state.ol} label="Numbered list">
          <Icon.ListNumbers />
        </Button>
        <Button cmd="quote" on={state.quote} label="Quote">
          <Icon.Quote />
        </Button>
        <Button cmd="clear" label="Clear formatting">
          <Icon.ClearFormat />
        </Button>
        <span class="grow" />
        <button
          type="button"
          class="btn ghost small"
          disabled={p.disabled}
          title="Switch this message to plain text"
          onClick={p.onPlainText}
        >
          Plain text
        </button>
      </div>
      {linking && (
        <form
          class="editor-link"
          onSubmit={(e) => {
            e.preventDefault();
            confirmLink();
          }}
        >
          <input
            ref={linkInput}
            type="text"
            inputMode="url"
            placeholder="https://"
            aria-label="Link address"
            value={url}
            onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.stopPropagation();
                setLinking(false);
                send({ t: "focus" });
              }
            }}
          />
          <button type="submit" class="btn small">
            Add
          </button>
        </form>
      )}
      <iframe
        ref={frame}
        class="editor-frame"
        title="Message body"
        /*
          `allow-scripts` for the one script, and never `allow-same-origin`:
          the frame holds the original message's markup, and the two together
          would let it reach this document. `dev/parity.mjs` checks this
          attribute as it does the reader's.
        */
        sandbox="allow-scripts allow-modals"
        referrerpolicy="no-referrer"
        srcdoc={doc}
        onLoad={initialise}
        data-hash={EDITOR_HASH}
      />
    </div>
  );
}
